import { redirect, type LinkProps } from '@tanstack/react-router';
import type { MyEffectivePermissionsDto } from '@saas/api-client';
import { permissionsService, type PermissionKey } from '@/lib/services/permissions.service';
import type { GlobalStore } from '@/stores/global.store';
import type { QueryClient } from '@tanstack/react-query';

const PERMISSIONS_STALE_TIME = 5 * 60 * 1000;

export type PermissionGuardContext = { globalStore: GlobalStore; queryClient: QueryClient };

/**
 * Resolves the current user's effective permission set through the shared React
 * Query cache (same key `usePermissions` reads), so a route check warms the page
 * and vice versa. Returns null when there is no current company or the fetch
 * fails — callers treat null as unresolved and fail open, mirroring the
 * cosmetic-gating contract of `usePermissions`; the server enforces.
 */
export const ensureMyEffectivePermissions = async (
	context: PermissionGuardContext,
): Promise<MyEffectivePermissionsDto | null> => {
	if (!context.globalStore.getState().currentCompany?.id) return null;

	return context.queryClient
		.ensureQueryData({
			...permissionsService.useMyEffectivePermissions.queryOptions({}),
			staleTime: PERMISSIONS_STALE_TIME,
		})
		.catch(() => null);
};

interface RequireAnyPermissionArgs {
	context: PermissionGuardContext;
	anyOf: readonly PermissionKey[];
	redirectTo?: string;
}

/**
 * Route-level permission gate: redirects unless the user is a bypass role or
 * holds at least one of `anyOf`. Companion to `usePermissions` for nav gating,
 * so route and sidebar enforcement read the same effective set.
 */
export const requireAnyPermission = async ({
	context,
	anyOf,
	redirectTo = '/settings/profile',
}: RequireAnyPermissionArgs) => {
	// A fresh login deep-linking before the App effect selects a company reaches
	// beforeLoad with no current company; permissions are unresolvable then, so
	// fail open per the cosmetic-gating contract. The server enforces.
	if (!context.globalStore.getState().currentCompany?.id) return;

	const effective = await ensureMyEffectivePermissions(context);
	if (!effective || effective.isBypass) return;

	if (!anyOf.some((key) => effective.permissions.includes(key))) {
		throw redirect({ to: redirectTo });
	}
};

export interface PermittedPathCandidate {
	to: LinkProps['to'];
	anyOf: readonly PermissionKey[];
}

interface ResolveFirstPermittedPathArgs {
	context: PermissionGuardContext;
	candidates: readonly PermittedPathCandidate[];
	fallback: LinkProps['to'];
}

/**
 * Picks the first candidate path whose permission set the user can reach, so an
 * index redirect lands on an area the user actually has access to. Unresolved
 * permissions or a bypass role fall open to the first candidate, matching the
 * cosmetic-gating contract.
 */
export const resolveFirstPermittedPath = async ({
	context,
	candidates,
	fallback,
}: ResolveFirstPermittedPathArgs): Promise<LinkProps['to']> => {
	const first = candidates[0]?.to ?? fallback;
	if (!context.globalStore.getState().currentCompany?.id) return first;

	const effective = await ensureMyEffectivePermissions(context);
	if (!effective || effective.isBypass) return first;

	const permitted = candidates.find((candidate) => candidate.anyOf.some((key) => effective.permissions.includes(key)));
	return permitted?.to ?? fallback;
};
